import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/firebase/config';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { login } from '@/lib/actions'; // Importamos la acción de login

export default function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    if (!acceptedTerms) {
      setError("Debes aceptar la Política de Privacidad y confirmar que tienes al menos 14 años.");
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      // 1. Crear el usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // 2. Crear el documento de usuario en Firestore
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        role: 'user', // Rol por defecto
        createdAt: new Date(),
        nickname: user.email?.split('@')[0] || 'Nuevo Usuario',
        profileImageUrl: '',
      });

      // 3. Iniciar sesión y crear la cookie de sesión (en desuso, pero se mantiene si se necesita)
      // En una SPA, el estado de onAuthStateChanged es suficiente.
      navigate('/jartinet/home');

    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        setError('Este correo electrónico ya está en uso.');
      } else if (error.code === 'auth/weak-password') {
        setError('La contraseña es demasiado débil. Debe tener al menos 6 caracteres.');
      } else {
        setError('Ocurrió un error al registrar la cuenta.');
        console.error(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-sm text-center">
        <CardHeader>
          <CardTitle>Crear Cuenta</CardTitle>
          <CardDescription>Crea una nueva cuenta para acceder a Jartinet.</CardDescription>
        </CardHeader>
        <form onSubmit={handleRegister}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2 text-left">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2 text-left">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2 text-left">
              <Label htmlFor="confirm-password">Confirmar Contraseña</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <div className="flex items-start gap-2 text-left">
              <input
                id="accept-terms"
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 accent-primary"
                required
              />
              <Label htmlFor="accept-terms" className="text-xs font-normal text-muted-foreground leading-snug">
                Tengo al menos 14 años y he leído y acepto la{' '}
                <Link to="/legal/politica-de-privacidad" target="_blank" className="underline hover:text-primary">Política de Privacidad</Link>{' '}
                y el{' '}
                <Link to="/legal/aviso-legal" target="_blank" className="underline hover:text-primary">Aviso Legal</Link>.
              </Label>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading || !acceptedTerms}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Registrarse
            </Button>
          </CardContent>
        </form>
        <CardFooter className="flex-col text-sm text-muted-foreground">
           <p>¿Ya tienes una cuenta?</p>
            <Button variant="link" asChild>
                <Link to="/login">Inicia sesión</Link>
            </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
